import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, Observable, finalize, map, of, shareReplay, tap } from 'rxjs';
import { FeatureEnum } from '../../enum/feature.enum';
import { isMinimumOfficialPlan } from '../../guards/plan/minimum-plan';
import { MeApiResult, LimitUsage, MeOverview } from '../../models/auth/new-me.model';

export interface ApiResponseLimites {
    data: {
        limites: LimitUsage[];
    };
    errors: unknown;
    message: string;
    success: boolean;
}

export interface FeatureUsage extends LimitUsage {
    nomeExibicao?: string;
    bloqueado?: boolean;
}

/** Feature presente no /new-me e com saldo (disponivel null ou maior que 0). */
export function hasQuotaToConsume(limit: LimitUsage | null | undefined): boolean {
    return limit != null && (limit.disponivel === null || limit.disponivel > 0);
}

@Injectable({
    providedIn: 'root',
})
export class UsageLimitService {
    private urlBase = environment.urlApi;

    private usageSubject = new BehaviorSubject<LimitUsage[]>([]);
    public usage$ = this.usageSubject.asObservable();

    private refreshUsageRequest$: Observable<ApiResponseLimites> | null = null;

    /** Último overview /new-me — AuthService reutiliza no hydrate. */
    private lastNewMeOverview: MeOverview | null = null;

    constructor(private httpClient: HttpClient) {}

    public getLastNewMeOverview(): MeOverview | null {
        return this.lastNewMeOverview;
    }

    public isMinimumPlan(): boolean {
        return isMinimumOfficialPlan(this.lastNewMeOverview?.officialPlan);
    }

    public applyLimitesFromNewMe(limites: LimitUsage[]): void {
        this.usageSubject.next(limites);
    }

    public refreshUsage(): Observable<ApiResponseLimites> {
        if (this.refreshUsageRequest$) {
            return this.refreshUsageRequest$;
        }

        this.refreshUsageRequest$ = this.httpClient.get<MeApiResult>(`${this.urlBase}/new-me`).pipe(
            tap((result) => {
                if (result.success && result.data) {
                    const limites = Array.isArray(result.data.limites) ? result.data.limites : [];
                    this.lastNewMeOverview = { ...result.data, limites };
                    this.usageSubject.next(limites);
                }
            }),
            map(
                (result): ApiResponseLimites => ({
                    success: result.success,
                    message: result.message,
                    errors: result.errors,
                    data: {
                        limites: Array.isArray(result.data?.limites) ? result.data.limites : [],
                    },
                }),
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
            finalize(() => {
                this.refreshUsageRequest$ = null;
            }),
        );

        return this.refreshUsageRequest$;
    }

    public ensureUsageLoaded(): Observable<ApiResponseLimites | null> {
        if (this.usageSubject.value.length > 0) {
            return of(null);
        }
        return this.refreshUsage();
    }

    public getCurrentUsage(): LimitUsage[] {
        return this.usageSubject.value;
    }

    public getLimit(featureKey: FeatureEnum): LimitUsage | undefined {
        return this.usageSubject.value.find((row) => row.feature === featureKey);
    }

    public canUseFeature(featureKey: FeatureEnum): boolean {
        const feature = this.getLimit(featureKey);
        return hasQuotaToConsume(feature);
    }

    public applyLocalUsage(featureKey: FeatureEnum, delta: 1 | -1): void {
        const current = this.getLimit(featureKey);
        if (!current || current.ilimitado || current.disponivel === null) {
            return;
        }
        if (delta < 0 && current.tipo_reset !== 'acumulado') {
            return;
        }

        const consumo_atual = Math.max(0, current.consumo_atual + delta);
        const disponivel = Math.max(0, current.disponivel - delta);
        const limite = current.limite_mensal ?? 0;
        const updated: LimitUsage = {
            ...current,
            consumo_atual,
            disponivel,
            porcentagem_uso: limite > 0 ? Math.round((consumo_atual / limite) * 100) : current.porcentagem_uso,
        };

        const limites = this.usageSubject.value.map((row) => (row.feature === featureKey ? updated : row));
        this.usageSubject.next(limites);
    }

    public deleteCacheUsage(clienteId: string): Observable<unknown> {
        return this.httpClient.get<unknown>(`${this.urlBase}/cliente/${clienteId}/delete-cache-usage`);
    }
}
